// vg-binding: CONTEXT:decisions  (P999.11.D-09/D-14/D-15/D-16 tokens + D-04 dark-ok)
// vg-binding: INTERFACE-STANDARDS:error-shape  (pure FE restyle — no API surface changed)
// apps/admin/src/pages/billing/ChargebacksPage.tsx
//
// Phase 3.2 Wave 11 Task 33 — Admin Chargebacks list + detail view.
//
// Per CONTEXT.md (Fraud Prevention & Chargeback).
// Covers goal: G-CB-05.
//
// Endpoints (per API-CONTRACTS §Chargeback):
//   GET /api/v1/admin/chargebacks?status=&merchant=&cursor=&limit=
//   GET /api/v1/admin/chargebacks/:id
//   POST /api/v1/admin/chargebacks/webhook — server-side only, NOT consumed.
//
// Acceptance criteria covered:
//   ✓ Chargeback detail shows debt remaining + countdown to 7d deadline
//
// Design fidelity (admin-chargebacks-list.png — SHA256 e3f22df…):
//   - Page header per case: "Case CB-2026-0042" + status pill, USD amount
//     (rose tone) at top-right with deadline countdown chip beneath.
//   - Two-column layout: left "Case Details" card (gateway, ID, reason
//     code rose accent, original charge link, merchant orange link,
//     frozen amount rose, recovery progress amber); right column has
//     "Timeline" card (event dots) + "Actions" card (Resolve manually
//     orange filled, Add admin note neutral, Mark as lost destructive
//     outline).
//   - List view (when no case selected): status filter chips + a row
//     table for chargeback_cases.

import { useCallback, useEffect, useState } from 'react';
import {
  Button,
  Dialog,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  cn,
  useToast,
  PageHeader,
} from '@printway/ui';
import { apiClient } from '@printway/shared/api';
import { BillingStatusBadge } from '../../components/billing/BillingStatusBadge.js';
import { BackLink } from '../../components/ui/BackLink.js';
import { useTranslation } from '../../i18n/useTranslation.js';

type TFn = (key: string, vars?: Record<string, unknown>) => string;

// ---------------------------------------------------------------------------
// Loose POST client — matches the same any-shape escape hatch used by the
// detail GET above. The shared API client doesn't have typed POST helpers
// for these two new admin endpoints yet.
// ---------------------------------------------------------------------------
type LoosePostClient = {
  POST: (
    path: string,
    init: { body?: unknown; headers?: Record<string, string> },
  ) => Promise<{ data?: unknown; error?: unknown; response: Response }>;
};

// ---------------------------------------------------------------------------
// Wire types — slim FE mirror of API-CONTRACTS L1862-1873 + L1904-1920.
// Phase 3.2 declares per-case `outstanding_debt` account; the API
// surface doesn't return it directly, so the FE computes
// `debt_remaining = amount_usd_cents - recovered_amount_cents` from the
// shape that the BE returns (recovered_amount_cents is part of the case
// detail worker bookkeeping).
// ---------------------------------------------------------------------------

type ChargebackStatus =
  | 'open'
  | 'investigating'
  | 'resolved_won'
  | 'resolved_lost'
  //  fraud life-cycle states surfaced via task spec:
  | 'disputed'
  | 'resolved'
  | 'debt_outstanding'
  | 'frozen_pending_recovery';

interface ChargebackCase {
  id: string;
  merchant_id: string;
  merchant_name: string;
  topup_request_id: string | null;
  gateway: string;
  external_chargeback_id: string;
  amount_usd_cents: number;
  recovered_amount_cents?: number;
  status: ChargebackStatus;
  reason_code?: string | null;
  opened_at: string;
  //  deadline = chargebackAt + 7d. BE may emit response_deadline OR
  // we compute from opened_at + 7d.
  response_deadline?: string | null;
  resolved_at: string | null;
}

interface ChargebackDetail extends ChargebackCase {
  evidence_files: { file_id: string; label: string; uploaded_at: string }[];
  admin_notes: { admin_id: string; note: string; created_at: string }[];
  webhook_events?: { ts: string; type: string; external_id: string }[];
}

interface SuccessEnvelope<T> {
  success: true;
  data: T;
  requestId: string;
}
interface ErrorEnvelope {
  success: false;
  error: { code: string; message: string };
  requestId: string;
}

function unwrap<T>(payload: unknown): T {
  if (
    payload &&
    typeof payload === 'object' &&
    'success' in payload &&
    (payload as SuccessEnvelope<T>).success === true &&
    'data' in payload
  ) {
    return (payload as SuccessEnvelope<T>).data;
  }
  return payload as T;
}

function envelopeError(payload: unknown): string {
  if (
    payload &&
    typeof payload === 'object' &&
    'success' in payload &&
    (payload as ErrorEnvelope).success === false &&
    'error' in payload
  ) {
    return (payload as ErrorEnvelope).error?.message ?? 'Unknown error';
  }
  return 'Network error — check connection';
}

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

function formatUsd(cents: number): string {
  const value = cents / 100;
  return new Intl.NumberFormat(undefined, {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: 2,
  }).format(value);
}

const SEVEN_DAYS_MS = 7 * 24 * 60 * 60 * 1000;

interface DeadlineRemaining {
  expired: boolean;
  daysLeft: number;
  hoursLeft: number;
}

/**
 * Compute remaining time until 7d deadline from chargeback open time.
 * deadline = chargebackAt + 7*24*3600.
 */
function computeDeadline(openedAt: string, deadline?: string | null): DeadlineRemaining {
  const opened = new Date(openedAt).getTime();
  const target = deadline ? new Date(deadline).getTime() : opened + SEVEN_DAYS_MS;
  const remainingMs = target - Date.now();
  if (remainingMs <= 0) {
    return { expired: true, daysLeft: 0, hoursLeft: 0 };
  }
  const totalHours = Math.floor(remainingMs / (60 * 60 * 1000));
  const daysLeft = Math.floor(totalHours / 24);
  const hoursLeft = totalHours - daysLeft * 24;
  return { expired: false, daysLeft, hoursLeft };
}

/** Localized "Xh" or "Xd Yh" (no "remaining" suffix). */
function formatTimeLeft(d: DeadlineRemaining, t: TFn): string {
  return d.daysLeft === 0
    ? t('chargeback.deadline.hoursOnly', { hours: d.hoursLeft })
    : t('chargeback.deadline.daysHours', { days: d.daysLeft, hours: d.hoursLeft });
}

/** Localized deadline display ("X remaining" or "Deadline passed"). */
function formatDeadline(d: DeadlineRemaining, t: TFn): string {
  return d.expired
    ? t('chargeback.deadline.passed')
    : t('chargeback.deadline.remaining', { time: formatTimeLeft(d, t) });
}

function timeAgo(iso: string, t: TFn): string {
  const ms = Date.now() - new Date(iso).getTime();
  if (Number.isNaN(ms) || ms < 0) return '—';
  const days = Math.floor(ms / (24 * 60 * 60 * 1000));
  if (days >= 1)
    return days === 1
      ? t('chargeback.timeAgo.dayAgo', { count: days })
      : t('chargeback.timeAgo.daysAgo', { count: days });
  const hrs = Math.floor(ms / (60 * 60 * 1000));
  if (hrs >= 1)
    return hrs === 1
      ? t('chargeback.timeAgo.hourAgo', { count: hrs })
      : t('chargeback.timeAgo.hoursAgo', { count: hrs });
  return t('chargeback.timeAgo.justNow');
}

// ---------------------------------------------------------------------------
// Status filters — task body lists open / disputed / resolved / debt_outstanding
// ---------------------------------------------------------------------------

interface StatusFilter {
  key: 'all' | 'open' | 'disputed' | 'resolved' | 'debt_outstanding';
  apiValues: ChargebackStatus[];
}

const STATUS_FILTERS: StatusFilter[] = [
  { key: 'all', apiValues: [] },
  { key: 'open', apiValues: ['open'] },
  { key: 'disputed', apiValues: ['investigating', 'disputed'] },
  {
    key: 'resolved',
    apiValues: ['resolved', 'resolved_won', 'resolved_lost'],
  },
  {
    key: 'debt_outstanding',
    apiValues: ['debt_outstanding', 'frozen_pending_recovery'],
  },
];

// ---------------------------------------------------------------------------
// Detail view (per design — Case Details + Timeline + Actions)
// ---------------------------------------------------------------------------

function ChargebackDetailView({
  caseId,
  onBack,
}: {
  caseId: string;
  onBack: () => void;
}): JSX.Element {
  const { toast } = useToast();
  const { t } = useTranslation('finance');
  const [detail, setDetail] = useState<ChargebackDetail | null>(null);
  const [loading, setLoading] = useState(true);
  // Tick state forces 1-min re-render so countdown doesn't go stale on
  // long-open detail views. (Per-second is overkill for day-granularity.)
  const [, setTick] = useState(0);
  // Reload counter — bumped after a successful resolve / note POST so the
  // detail GET re-runs. Cheaper than introducing TanStack Query here for
  // one query.
  const [reloadKey, setReloadKey] = useState(0);

  // Dialog state for the 3 admin actions.
  const [resolveOpen, setResolveOpen] = useState(false);
  const [noteOpen, setNoteOpen] = useState(false);
  const [lostOpen, setLostOpen] = useState(false);

  useEffect(() => {
    let cancelled = false;
    async function load(): Promise<void> {
      setLoading(true);
      try {
        const client = apiClient();
        const path = `/api/v1/admin/chargebacks/${encodeURIComponent(caseId)}`;
        const { data, error, response } = await (
          client as unknown as {
            GET: (p: string) => Promise<{
              data?: unknown;
              error?: unknown;
              response: Response;
            }>;
          }
        ).GET(path);
        if (cancelled) return;
        if (error || !response.ok) {
          toast({
            title: t('chargeback.toast.loadDetailFailed'),
            description: envelopeError(error),
            variant: 'error',
          });
          setDetail(null);
          return;
        }
        setDetail(unwrap<ChargebackDetail>(data));
      } catch (err) {
        if (cancelled) return;
        toast({
          title: t('chargeback.toast.networkError'),
          description: err instanceof Error ? err.message : t('chargeback.toast.checkConnection'),
          variant: 'error',
        });
        setDetail(null);
      } finally {
        if (!cancelled) setLoading(false);
      }
    }
    void load();
    return () => {
      cancelled = true;
    };
  }, [caseId, toast, reloadKey, t]);

  // Tick every minute for deadline countdown freshness.
  useEffect(() => {
    const id = setInterval(() => setTick((n) => n + 1), 60_000);
    return () => clearInterval(id);
  }, []);

  if (loading) {
    return (
      <main className="flex flex-col gap-6 p-6">
        <BackLink onClick={onBack} label={t('chargeback.detail.backToList')} testId="chargeback-detail-loading-back" />
        <div className="rounded-xl border border-pw-border bg-pw-bg p-12 text-center text-pw-text-muted">
          {t('chargeback.detail.loading')}
        </div>
      </main>
    );
  }
  if (!detail) {
    return (
      <main className="flex flex-col gap-6 p-6">
        <BackLink onClick={onBack} label={t('chargeback.detail.backToList')} testId="chargeback-detail-empty-back" />
        <div className="rounded-xl border border-pw-border bg-pw-bg p-12 text-center text-pw-text-muted">
          {t('chargeback.detail.notFound')}
        </div>
      </main>
    );
  }

  const recovered = detail.recovered_amount_cents ?? 0;
  const total = detail.amount_usd_cents;
  const debtRemaining = Math.max(0, total - recovered);
  const recoveryPct = total === 0 ? 0 : Math.min(100, Math.round((recovered / total) * 100));
  const deadline = computeDeadline(detail.opened_at, detail.response_deadline);

  const isFrozen =
    detail.status === 'frozen_pending_recovery' ||
    detail.status === 'debt_outstanding' ||
    detail.status === 'open';

  return (
    <main className="flex flex-col gap-6 p-6" data-testid="chargeback-detail">
      <BackLink onClick={onBack} label={t('chargeback.detail.back')} testId="chargeback-detail-back" />

      {/* Page header */}
      <PageHeader
        title={
          <>
            {t('chargeback.detail.casePrefix')} {detail.external_chargeback_id || `CB-${detail.id.slice(-4)}`}
          </>
        }
        titleAddon={
          <span
            className={cn(
              'inline-flex h-7 items-center rounded-md px-2.5 text-xs font-semibold',
              isFrozen
                ? 'bg-rose-500 text-white'
                : detail.status === 'resolved_won' || detail.status === 'resolved'
                  ? 'bg-emerald-500 text-white'
                  : 'bg-amber-500 text-white',
            )}
          >
            {t(`chargeback.status.${detail.status}`)}
          </span>
        }
        subtitle={t('chargeback.detail.subtitle', {
          gateway: detail.gateway,
          amount: formatUsd(detail.amount_usd_cents),
          opened: timeAgo(detail.opened_at, t),
        })}
        actions={
          <div className="text-right">
            <p className="text-3xl font-semibold text-rose-600 dark:text-rose-300">
              {formatUsd(detail.amount_usd_cents)}
            </p>
            <span
              className={cn(
                'mt-2 inline-flex h-7 items-center gap-1 rounded-md border px-2.5 text-xs font-medium',
                deadline.expired
                  ? 'border-rose-500 bg-rose-50 dark:bg-rose-950/40 text-rose-700 dark:text-rose-300'
                  : deadline.daysLeft <= 2
                    ? 'border-rose-300 bg-rose-50 dark:bg-rose-950/40 text-rose-700 dark:text-rose-300'
                    : 'border-amber-300 bg-amber-50 dark:bg-amber-950/40 text-amber-700 dark:text-amber-300',
              )}
              data-testid="chargeback-deadline"
            >
              <ClockIcon />
              {t('chargeback.detail.recoveryDeadline', { value: formatDeadline(deadline, t) })}
            </span>
          </div>
        }
      />

      {/* 2-column body */}
      <div className="grid gap-6 lg:grid-cols-[1.6fr_1fr]">
        {/* LEFT — Case Details */}
        <section className="rounded-xl border border-pw-border bg-pw-bg p-6">
          <h2 className="text-lg font-semibold text-pw-text">{t('chargeback.detail.caseDetails')}</h2>
          <dl className="mt-4 space-y-3 text-sm">
            <DetailRow label={t('chargeback.detail.gateway')} value={detail.gateway} valueClassName="font-semibold" />
            <DetailRow
              label={t('chargeback.detail.chargebackId')}
              value={detail.external_chargeback_id || detail.id}
              valueClassName="font-mono"
            />
            <DetailRow
              label={t('chargeback.detail.reasonCode')}
              value={detail.reason_code || '—'}
              valueClassName="text-rose-600 dark:text-rose-300"
            />
            {detail.topup_request_id ? (
              <DetailRow
                label={t('chargeback.detail.originalCharge')}
                value={`${detail.topup_request_id} →`}
                valueClassName="text-pw-primary"
              />
            ) : null}
            <DetailRow
              label={t('chargeback.detail.merchant')}
              value={detail.merchant_name}
              valueClassName="text-pw-primary"
            />
            <DetailRow
              label={t('chargeback.detail.frozenAmount')}
              value={formatUsd(detail.amount_usd_cents)}
              valueClassName="font-semibold text-rose-600 dark:text-rose-300"
            />
            <div className="pt-2">
              <div className="flex items-center justify-between text-sm">
                <span className="text-pw-text-muted">{t('chargeback.detail.recoveryProgress')}</span>
                <span className="font-semibold text-amber-600 dark:text-amber-300" data-testid="recovery-progress">
                  {formatUsd(recovered)} / {formatUsd(total)} ({recoveryPct}%)
                </span>
              </div>
              <div
                className="mt-2 h-2 w-full overflow-hidden rounded-full bg-pw-surface"
                role="progressbar"
                aria-valuenow={recoveryPct}
                aria-valuemin={0}
                aria-valuemax={100}
              >
                <div
                  className="h-full bg-amber-500 transition-all"
                  style={{ width: `${recoveryPct}%` }}
                />
              </div>
            </div>
            {/* Debt remaining row — explicitly required by AC. */}
            <div className="mt-3 flex items-center justify-between rounded-md bg-rose-50 dark:bg-rose-950/40 px-3 py-2 text-sm">
              <span className="font-medium text-rose-700 dark:text-rose-300">{t('chargeback.detail.debtRemaining')}</span>
              <span
                className="font-semibold text-rose-700 dark:text-rose-300"
                data-testid="chargeback-debt-remaining"
              >
                {formatUsd(debtRemaining)}
              </span>
            </div>
          </dl>
        </section>

        {/* RIGHT — Timeline + Actions */}
        <aside className="flex flex-col gap-6">
          <section className="rounded-xl border border-pw-border bg-pw-bg p-6">
            <h2 className="text-lg font-semibold text-pw-text">{t('chargeback.timeline.heading')}</h2>
            <ol className="mt-4 space-y-4">
              <TimelineItem
                tone="rose"
                title={t('chargeback.timeline.opened')}
                detail={t('chargeback.timeline.openedDetail', {
                  ago: timeAgo(detail.opened_at, t),
                  amount: formatUsd(detail.amount_usd_cents),
                })}
              />
              {(detail.webhook_events ?? []).map((ev, idx) => (
                <TimelineItem
                  key={`${ev.external_id}-${idx}`}
                  tone="amber"
                  title={ev.type.replace(/_/g, ' ')}
                  detail={t('chargeback.timeline.eventDetail', {
                    ago: timeAgo(ev.ts, t),
                    id: ev.external_id,
                  })}
                />
              ))}
              {!deadline.expired ? (
                <TimelineItem
                  tone="neutral"
                  title={t('chargeback.timeline.approaching')}
                  detail={t('chargeback.timeline.approachingDetail', {
                    time: formatTimeLeft(deadline, t),
                    debt: formatUsd(debtRemaining),
                  })}
                />
              ) : (
                <TimelineItem
                  tone="rose"
                  title={t('chargeback.timeline.passed')}
                  detail={t('chargeback.timeline.passedDetail', { debt: formatUsd(debtRemaining) })}
                />
              )}
            </ol>
          </section>

          <section className="rounded-xl border border-pw-border bg-pw-bg p-6">
            <h2 className="text-lg font-semibold text-pw-text">{t('chargeback.actions.heading')}</h2>
            <div className="mt-4 flex flex-col gap-2">
              <Button
                variant="default"
                onClick={() => setResolveOpen(true)}
                data-testid="chargeback-action-resolve"
              >
                {t('chargeback.actions.resolve')}
              </Button>
              <Button
                variant="secondary"
                onClick={() => setNoteOpen(true)}
                data-testid="chargeback-action-add-note"
              >
                {t('chargeback.actions.addNote')}
              </Button>
              <Button
                variant="outline"
                onClick={() => setLostOpen(true)}
                className="border-rose-300 text-rose-600 dark:text-rose-300 hover:bg-rose-50"
                data-testid="chargeback-action-mark-lost"
              >
                {t('chargeback.actions.markLost')}
              </Button>
            </div>
            <p className="mt-3 text-xs text-pw-text-muted">
              {t('chargeback.actions.note')}
            </p>
          </section>
        </aside>
      </div>

      <ResolveChargebackDialog
        open={resolveOpen}
        onOpenChange={setResolveOpen}
        caseId={caseId}
        onResolved={() => {
          setReloadKey((n) => n + 1);
        }}
      />
      <AddNoteDialog
        open={noteOpen}
        onOpenChange={setNoteOpen}
        caseId={caseId}
        onSaved={() => {
          setReloadKey((n) => n + 1);
        }}
      />
      <MarkLostDialog
        open={lostOpen}
        onOpenChange={setLostOpen}
        caseId={caseId}
        externalId={detail.external_chargeback_id || detail.id}
        onResolved={() => {
          setReloadKey((n) => n + 1);
        }}
      />
    </main>
  );
}

// ---------------------------------------------------------------------------
// Action dialogs
// ---------------------------------------------------------------------------

interface ResolveDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  caseId: string;
  onResolved: () => void;
}

type ResolveOutcome = 'won' | 'resolved' | 'lost' | 'expired';

function ResolveChargebackDialog({
  open,
  onOpenChange,
  caseId,
  onResolved,
}: ResolveDialogProps): JSX.Element | null {
  const { toast } = useToast();
  const { t } = useTranslation('finance');
  const [outcome, setOutcome] = useState<ResolveOutcome>('resolved');
  const [note, setNote] = useState('');
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (!open) {
      setOutcome('resolved');
      setNote('');
      setSubmitting(false);
    }
  }, [open]);

  async function submit(): Promise<void> {
    setSubmitting(true);
    try {
      const client = apiClient() as unknown as LoosePostClient;
      const body: Record<string, unknown> = { outcome };
      const trimmed = note.trim();
      if (trimmed) body.note = trimmed;
      const { error, response } = await client.POST(
        `/api/v1/admin/chargebacks/${encodeURIComponent(caseId)}/resolve`,
        { body, headers: { 'Content-Type': 'application/json' } },
      );
      if (error || !response.ok) {
        toast({
          title: t('chargeback.resolve.toast.failed'),
          description: envelopeError(error),
          variant: 'error',
        });
        return;
      }
      toast({
        title: t('chargeback.resolve.toast.success'),
        description: t('chargeback.resolve.toast.successDesc', {
          outcome: t(`chargeback.outcome.${outcome}`),
        }),
        variant: 'success',
      });
      onOpenChange(false);
      onResolved();
    } catch (err) {
      toast({
        title: t('chargeback.toast.networkError'),
        description: err instanceof Error ? err.message : t('chargeback.toast.checkConnection'),
        variant: 'error',
      });
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <Dialog
      open={open}
      onOpenChange={(next) => {
        if (submitting) return;
        onOpenChange(next);
      }}
      className="max-w-md"
    >
      <div
        data-testid="chargeback-resolve-dialog"
        className="flex flex-col gap-4"
      >
        <DialogHeader>
          <DialogTitle>{t('chargeback.resolve.title')}</DialogTitle>
        </DialogHeader>

        <form
          onSubmit={(e) => {
            e.preventDefault();
            if (!submitting) void submit();
          }}
          className="flex flex-col gap-4"
        >
          <div className="flex flex-col gap-1.5">
            <label
              htmlFor="cb-resolve-outcome"
              className="text-sm font-medium text-pw-text"
            >
              {t('chargeback.resolve.outcomeLabel')} <span className="text-red-600 dark:text-red-300">*</span>
            </label>
            <select
              id="cb-resolve-outcome"
              data-testid="chargeback-resolve-outcome"
              value={outcome}
              onChange={(e) => setOutcome(e.target.value as ResolveOutcome)}
              disabled={submitting}
              className="rounded-md border border-pw-border bg-pw-bg px-3 py-2 text-sm text-pw-text outline-none focus:border-pw-primary focus:ring-1 focus:ring-pw-primary"
            >
              <option value="won">{t('chargeback.resolve.outcomeWon')}</option>
              <option value="resolved">{t('chargeback.resolve.outcomeResolved')}</option>
              <option value="lost">{t('chargeback.resolve.outcomeLost')}</option>
              <option value="expired">{t('chargeback.resolve.outcomeExpired')}</option>
            </select>
            <p className="text-xs text-pw-text-muted">
              {t('chargeback.resolve.terminalHint')}
            </p>
          </div>

          <div className="flex flex-col gap-1.5">
            <label
              htmlFor="cb-resolve-note"
              className="text-sm font-medium text-pw-text"
            >
              {t('chargeback.resolve.noteLabel')}
            </label>
            <textarea
              id="cb-resolve-note"
              data-testid="chargeback-resolve-note"
              value={note}
              onChange={(e) => setNote(e.target.value)}
              placeholder={t('chargeback.resolve.notePlaceholder')}
              maxLength={500}
              rows={3}
              disabled={submitting}
              className="rounded-md border border-pw-border bg-pw-bg px-3 py-2 text-sm text-pw-text outline-none focus:border-pw-primary focus:ring-1 focus:ring-pw-primary"
            />
          </div>

          <DialogFooter>
            <Button
              type="button"
              variant="ghost"
              onClick={() => onOpenChange(false)}
              disabled={submitting}
            >
              {t('chargeback.cancel')}
            </Button>
            <Button
              type="submit"
              variant="default"
              disabled={submitting}
              data-testid="chargeback-resolve-submit"
            >
              {submitting ? t('chargeback.resolve.submitting') : t('chargeback.resolve.submit')}
            </Button>
          </DialogFooter>
        </form>
      </div>
    </Dialog>
  );
}

interface AddNoteDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  caseId: string;
  onSaved: () => void;
}

function AddNoteDialog({
  open,
  onOpenChange,
  caseId,
  onSaved,
}: AddNoteDialogProps): JSX.Element | null {
  const { toast } = useToast();
  const { t } = useTranslation('finance');
  const [note, setNote] = useState('');
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (!open) {
      setNote('');
      setSubmitting(false);
    }
  }, [open]);

  async function submit(): Promise<void> {
    const trimmed = note.trim();
    if (trimmed.length === 0) {
      toast({
        title: t('chargeback.addNote.toast.requiredTitle'),
        description: t('chargeback.addNote.toast.requiredDesc'),
        variant: 'error',
      });
      return;
    }
    setSubmitting(true);
    try {
      const client = apiClient() as unknown as LoosePostClient;
      const { error, response } = await client.POST(
        `/api/v1/admin/chargebacks/${encodeURIComponent(caseId)}/notes`,
        {
          body: { note: trimmed },
          headers: { 'Content-Type': 'application/json' },
        },
      );
      if (error || !response.ok) {
        toast({
          title: t('chargeback.addNote.toast.failed'),
          description: envelopeError(error),
          variant: 'error',
        });
        return;
      }
      toast({
        title: t('chargeback.addNote.toast.success'),
        description: t('chargeback.addNote.toast.successDesc'),
        variant: 'success',
      });
      onOpenChange(false);
      onSaved();
    } catch (err) {
      toast({
        title: t('chargeback.toast.networkError'),
        description: err instanceof Error ? err.message : t('chargeback.toast.checkConnection'),
        variant: 'error',
      });
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <Dialog
      open={open}
      onOpenChange={(next) => {
        if (submitting) return;
        onOpenChange(next);
      }}
      className="max-w-md"
    >
      <div
        data-testid="chargeback-add-note-dialog"
        className="flex flex-col gap-4"
      >
        <DialogHeader>
          <DialogTitle>{t('chargeback.addNote.title')}</DialogTitle>
        </DialogHeader>

        <form
          onSubmit={(e) => {
            e.preventDefault();
            if (!submitting) void submit();
          }}
          className="flex flex-col gap-4"
        >
          <div className="flex flex-col gap-1.5">
            <label
              htmlFor="cb-note-input"
              className="text-sm font-medium text-pw-text"
            >
              {t('chargeback.addNote.noteLabel')} <span className="text-red-600 dark:text-red-300">*</span>
            </label>
            <textarea
              id="cb-note-input"
              data-testid="chargeback-note-input"
              value={note}
              onChange={(e) => setNote(e.target.value)}
              placeholder={t('chargeback.addNote.notePlaceholder')}
              maxLength={500}
              minLength={1}
              rows={4}
              disabled={submitting}
              required
              className="rounded-md border border-pw-border bg-pw-bg px-3 py-2 text-sm text-pw-text outline-none focus:border-pw-primary focus:ring-1 focus:ring-pw-primary"
            />
            <p className="text-xs text-pw-text-muted">
              {t('chargeback.addNote.hint')}
            </p>
          </div>

          <DialogFooter>
            <Button
              type="button"
              variant="ghost"
              onClick={() => onOpenChange(false)}
              disabled={submitting}
            >
              {t('chargeback.cancel')}
            </Button>
            <Button
              type="submit"
              variant="default"
              disabled={submitting || note.trim().length === 0}
              data-testid="chargeback-note-submit"
            >
              {submitting ? t('chargeback.addNote.submitting') : t('chargeback.addNote.submit')}
            </Button>
          </DialogFooter>
        </form>
      </div>
    </Dialog>
  );
}

interface MarkLostDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  caseId: string;
  externalId: string;
  onResolved: () => void;
}

function MarkLostDialog({
  open,
  onOpenChange,
  caseId,
  externalId,
  onResolved,
}: MarkLostDialogProps): JSX.Element | null {
  const { toast } = useToast();
  const { t } = useTranslation('finance');
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (!open) setSubmitting(false);
  }, [open]);

  async function submit(): Promise<void> {
    setSubmitting(true);
    try {
      const client = apiClient() as unknown as LoosePostClient;
      const { error, response } = await client.POST(
        `/api/v1/admin/chargebacks/${encodeURIComponent(caseId)}/resolve`,
        {
          body: { outcome: 'lost' },
          headers: { 'Content-Type': 'application/json' },
        },
      );
      if (error || !response.ok) {
        toast({
          title: t('chargeback.markLost.toast.failed'),
          description: envelopeError(error),
          variant: 'error',
        });
        return;
      }
      toast({
        title: t('chargeback.markLost.toast.success'),
        description: t('chargeback.markLost.toast.successDesc', { id: externalId }),
        variant: 'success',
      });
      onOpenChange(false);
      onResolved();
    } catch (err) {
      toast({
        title: t('chargeback.toast.networkError'),
        description: err instanceof Error ? err.message : t('chargeback.toast.checkConnection'),
        variant: 'error',
      });
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <Dialog
      open={open}
      onOpenChange={(next) => {
        if (submitting) return;
        onOpenChange(next);
      }}
      className="max-w-md"
    >
      <div
        data-testid="chargeback-mark-lost-dialog"
        className="flex flex-col gap-4"
      >
        <DialogHeader>
          <DialogTitle className="text-rose-700 dark:text-rose-300">
            {t('chargeback.markLost.title')}
          </DialogTitle>
        </DialogHeader>

        <div className="rounded-md border border-rose-200 dark:border-rose-800 bg-rose-50 dark:bg-rose-950/40 px-4 py-3 text-sm text-rose-800 dark:text-rose-300">
          <p className="font-semibold">{t('chargeback.markLost.irreversible')}</p>
          <p className="mt-1">
            {t('chargeback.markLost.bodyPrefix')}{' '}
            <span className="font-mono">{externalId}</span>{' '}
            {t('chargeback.markLost.bodySuffix')}
          </p>
        </div>

        <DialogFooter>
          <Button
            type="button"
            variant="ghost"
            onClick={() => onOpenChange(false)}
            disabled={submitting}
          >
            {t('chargeback.cancel')}
          </Button>
          <Button
            type="button"
            variant="default"
            onClick={() => {
              if (!submitting) void submit();
            }}
            disabled={submitting}
            className="bg-rose-600 hover:bg-rose-700"
            data-testid="chargeback-mark-lost-confirm"
          >
            {submitting ? t('chargeback.markLost.submitting') : t('chargeback.markLost.confirm')}
          </Button>
        </DialogFooter>
      </div>
    </Dialog>
  );
}

function ClockIcon(): JSX.Element {
  return (
    <svg
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      className="h-3.5 w-3.5"
      aria-hidden="true"
    >
      <circle cx="12" cy="12" r="10" />
      <polyline points="12 6 12 12 16 14" />
    </svg>
  );
}

function DetailRow({
  label,
  value,
  valueClassName,
}: {
  label: string;
  value: string;
  valueClassName?: string;
}): JSX.Element {
  return (
    <div className="flex items-center justify-between gap-4">
      <span className="text-pw-text-muted">{label}</span>
      <span className={cn('text-right', valueClassName ?? 'text-pw-text')}>{value}</span>
    </div>
  );
}

function TimelineItem({
  tone,
  title,
  detail,
}: {
  tone: 'rose' | 'amber' | 'neutral';
  title: string;
  detail: string;
}): JSX.Element {
  const dotClass =
    tone === 'rose' ? 'bg-rose-500' : tone === 'amber' ? 'bg-amber-500' : 'bg-pw-border';
  return (
    <li className="flex gap-3">
      <span
        aria-hidden="true"
        className={cn('mt-1 h-2.5 w-2.5 shrink-0 rounded-full', dotClass)}
      />
      <div>
        <p className="text-sm font-semibold capitalize text-pw-text">{title}</p>
        <p className="text-xs text-pw-text-muted">{detail}</p>
      </div>
    </li>
  );
}

// ---------------------------------------------------------------------------
// List view
// ---------------------------------------------------------------------------

function ChargebackListView({
  onSelectCase,
}: {
  onSelectCase: (caseId: string) => void;
}): JSX.Element {
  const { toast } = useToast();
  const { t } = useTranslation('finance');
  const [filter, setFilter] = useState<StatusFilter['key']>(() => {
    if (typeof window === 'undefined') return 'all';
    const sp = new URLSearchParams(window.location.search).get('status');
    const match = STATUS_FILTERS.find((f) => f.key === sp);
    return (match?.key ?? 'all') as StatusFilter['key'];
  });
  const [rows, setRows] = useState<ChargebackCase[]>([]);
  const [loading, setLoading] = useState(false);

  // URL sync.
  useEffect(() => {
    if (typeof window === 'undefined') return;
    const params = new URLSearchParams(window.location.search);
    if (filter === 'all') params.delete('status');
    else params.set('status', filter);
    const qs = params.toString();
    const next = qs
      ? `${window.location.pathname}?${qs}`
      : window.location.pathname;
    window.history.replaceState(null, '', next);
  }, [filter]);

  const fetchList = useCallback(async (): Promise<void> => {
    setLoading(true);
    try {
      const client = apiClient();
      // Backend accepts a single status; for filter buckets that span
      // multiple BE statuses, we fall back to "all" + client-side filter.
      const target = STATUS_FILTERS.find((f) => f.key === filter);
      const params = new URLSearchParams();
      params.set('limit', '50');
      if (target && target.apiValues.length === 1 && target.apiValues[0]) {
        params.set('status', target.apiValues[0]);
      }
      const path = `/api/v1/admin/chargebacks?${params.toString()}`;
      const { data, error, response } = await (
        client as unknown as {
          GET: (p: string) => Promise<{
            data?: unknown;
            error?: unknown;
            response: Response;
          }>;
        }
      ).GET(path);
      if (error || !response.ok) {
        toast({
          title: t('chargeback.toast.loadListFailed'),
          description: envelopeError(error),
          variant: 'error',
        });
        setRows([]);
        return;
      }
      // Issue 7 fix (debug-bill iter 14, 2026-05-04): BE response shape is
      // `{success:true, data:[...cases], next_cursor}` — `unwrap()` already
      // returns the inner `data`, so previous `.data ?? []` was reaching for
      // a non-existent nested field on the array and resolving to []. Drop
      // the extra `.data` indirection.
      const rawList = (unwrap<unknown[]>(data) ?? []) as Array<Record<string, unknown>>;
      // BE serializer (admin.chargeback.ts:261) emits camelCase + alias names
      // (merchantId / amount / chargebackAt / responseDeadline / resolvedAt)
      // while FE shape is snake_case (merchant_id / amount_usd_cents /
      // opened_at / response_deadline / resolved_at). Map at this seam so
      // downstream rendering can stay typed against ChargebackCase.
      let payload: ChargebackCase[] = rawList.map((r) => ({
        id: String(r.id ?? ''),
        merchant_id: String(r.merchant_id ?? r.merchantId ?? ''),
        merchant_name: String(r.merchant_name ?? ''),
        topup_request_id: (r.topup_request_id as string | null) ?? null,
        gateway: String(r.gateway ?? ''),
        external_chargeback_id: String(
          r.external_chargeback_id ?? r.chargebackId ?? '',
        ),
        amount_usd_cents: Number(r.amount_usd_cents ?? r.amount ?? 0),
        recovered_amount_cents:
          typeof r.recovered_amount_cents === 'number'
            ? r.recovered_amount_cents
            : undefined,
        status: r.status as ChargebackStatus,
        reason_code: (r.reason_code as string | null | undefined) ?? null,
        opened_at: String(r.opened_at ?? r.chargebackAt ?? ''),
        response_deadline: String(
          r.response_deadline ?? r.responseDeadline ?? '',
        ),
        resolved_at: (r.resolved_at as string | null | undefined) ??
          (r.resolvedAt as string | null | undefined) ?? null,
      }));
      if (target && target.apiValues.length > 1) {
        payload = payload.filter((c) => target.apiValues.includes(c.status));
      }
      setRows(payload);
    } catch (err) {
      toast({
        title: t('chargeback.toast.networkError'),
        description: err instanceof Error ? err.message : t('chargeback.toast.checkConnection'),
        variant: 'error',
      });
      setRows([]);
    } finally {
      setLoading(false);
    }
  }, [filter, toast, t]);

  useEffect(() => {
    void fetchList();
  }, [fetchList]);

  return (
    <main className="flex flex-col gap-6 p-6" data-testid="admin-chargebacks">
      <PageHeader title={t('chargeback.list.heading')} subtitle={t('chargeback.list.subtitle')} />

      {/* Status chip filter — synced to URL ?status= */}
      <div className="flex flex-wrap gap-2" role="tablist" aria-label={t('chargeback.filters.ariaLabel')}>
        {STATUS_FILTERS.map((f) => {
          const isActive = filter === f.key;
          return (
            <button
              key={f.key}
              type="button"
              role="tab"
              aria-selected={isActive}
              onClick={() => setFilter(f.key)}
              className={cn(
                'inline-flex h-9 items-center rounded-full border px-4 text-xs font-semibold transition-colors',
                isActive
                  ? 'border-pw-primary bg-pw-primary text-white'
                  : 'border-pw-border bg-pw-bg text-pw-text-muted hover:bg-pw-surface',
              )}
              data-testid={`cb-filter-${f.key}`}
            >
              {t(`chargeback.filters.${f.key}`)}
            </button>
          );
        })}
      </div>

      {/* Cases table */}
      <div className="overflow-hidden rounded-xl border border-pw-border bg-pw-bg">
        <div className="grid grid-cols-[1.4fr_1.4fr_1fr_1fr_1fr_1fr] gap-4 border-b border-pw-border bg-rose-50 dark:bg-rose-950/40 px-6 py-3 text-xs font-medium uppercase tracking-wider text-pw-text-muted">
          <div>{t('chargeback.list.colCaseId')}</div>
          <div>{t('chargeback.list.colMerchant')}</div>
          <div>{t('chargeback.list.colGateway')}</div>
          <div>{t('chargeback.list.colAmount')}</div>
          <div>{t('chargeback.list.colStatus')}</div>
          <div>{t('chargeback.list.colDeadline')}</div>
        </div>
        {loading ? (
          <div className="flex h-32 items-center justify-center text-sm text-pw-text-muted">
            {t('chargeback.list.loading')}
          </div>
        ) : rows.length === 0 ? (
          <div className="flex h-32 items-center justify-center text-sm text-pw-text-muted">
            {t('chargeback.list.empty')}
          </div>
        ) : (
          <ul className="divide-y divide-pw-border">
            {rows.map((row) => {
              const deadline = computeDeadline(row.opened_at, row.response_deadline);
              return (
                <li
                  key={row.id}
                  aria-label={t('chargeback.list.rowAria', { id: row.external_chargeback_id || row.id, merchant: row.merchant_name })}
                  tabIndex={0}
                  className="grid cursor-pointer grid-cols-[1.4fr_1.4fr_1fr_1fr_1fr_1fr] items-center gap-4 px-6 py-4 hover:bg-pw-surface/50 focus:outline-none focus:ring-2 focus:ring-pw-primary"
                  onClick={onSelectCase.bind(null, row.id)} role={'button'}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' || e.key === ' ') {
                      e.preventDefault();
                      onSelectCase(row.id);
                    }
                  }}
                  data-testid={`cb-row-${row.id}`}
                >
                  <div className="font-mono text-sm text-pw-text">
                    {row.external_chargeback_id || row.id}
                  </div>
                  <div className="text-sm text-pw-primary">{row.merchant_name}</div>
                  <div className="text-sm text-pw-text">{row.gateway}</div>
                  <div className="text-sm font-semibold text-rose-600 dark:text-rose-300">
                    {formatUsd(row.amount_usd_cents)}
                  </div>
                  <div>
                    <BillingStatusBadge status={row.status} />
                  </div>
                  <div
                    className={cn(
                      'text-xs font-medium',
                      deadline.expired || deadline.daysLeft <= 2
                        ? 'text-rose-600 dark:text-rose-300'
                        : 'text-pw-text-muted',
                    )}
                  >
                    {formatDeadline(deadline, t)}
                  </div>
                </li>
              );
            })}
          </ul>
        )}
      </div>
    </main>
  );
}

// ---------------------------------------------------------------------------
// Main page — switches between list & detail
// ---------------------------------------------------------------------------

export function ChargebacksPage(): JSX.Element {
  // Detail selection synced to URL ?case= for shareable deep-links.
  const [selectedCase, setSelectedCase] = useState<string | null>(() => {
    if (typeof window === 'undefined') return null;
    return new URLSearchParams(window.location.search).get('case');
  });

  useEffect(() => {
    if (typeof window === 'undefined') return;
    const params = new URLSearchParams(window.location.search);
    if (selectedCase) params.set('case', selectedCase);
    else params.delete('case');
    const qs = params.toString();
    const next = qs
      ? `${window.location.pathname}?${qs}`
      : window.location.pathname;
    window.history.replaceState(null, '', next);
  }, [selectedCase]);

  if (selectedCase) {
    return (
      <ChargebackDetailView
        caseId={selectedCase}
        onBack={() => setSelectedCase(null)}
      />
    );
  }
  return <ChargebackListView onSelectCase={(id) => setSelectedCase(id)} />;
}

export default ChargebacksPage;
